import React, { memo, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  FinecoProductCardType,
  InformazioniGestoreType,
  ProductCardArrayDateProductReturnCategoryReturnType,
  ProductCardArrayDateValueType,
  ProductCardLabelWeightType,
  ProductCardRendimentiType,
} from "types/AssistantTypes";
import Anagrafica from "../components/Anagrafica";
import CurrencyExposureContainer from "../components/CurrencyExposureContainer";
import DrawdownChart from "../components/DrawdownChart";
import GenericContainer from "../components/GenericContainer";
import InformazioniGenerali from "../components/InformazioniGenerali";
import InformazioniGestore from "../components/InformazioniGestore";
import RendimentiChart from "../components/RendimentiChart";
import RendimentiPerAnnoSolareChart from "../components/RendimentiPerAnnoSolare";
import RiskTablesContainer from "../components/RiskTablesContainer";
import { FinecoProductCardButtons } from "./components/FinecoProductCardButtons";
import styles from "./finecoProductCard.module.scss";
import { ProductTypeEnum } from "types/ProductTypes";

type FinecoProductCardProps = {
  data: FinecoProductCardType;
};

const FinecoProductCard: React.FC<FinecoProductCardProps> = memo(({ data }) => {
  const { t } = useTranslation();
  const cardRef = useRef<HTMLDivElement>(null);

  const {
    informazioniPrincipali,
    anagrafica,
    rendimentiData,
    drawdownData,
    rendimentiPerAnnoSolareData,
    misureDiRischioData,
    rendimentiCumulatiData,
    distribuzioneAssetData,
    esposizioneValutariaData,
    esposizioneGeograficaData,
    esposizioneSettorialeParteAzionariaData,
    esposizioneSettorialeParteObbligazionariaData,
    title,
    date,
    isin,
  } = useMemo(() => {
    // Provide default empty arrays for all data to prevent undefined access
    const informazioniPrincipali = (data?.informazioni_principali ||
      []) as ProductCardLabelWeightType[];
    const anagrafica = (data?.anagrafica || []) as ProductCardLabelWeightType[];
    const rendimentiAndDrawDownData = (data?.rendimenti_e_drawdown ||
      {}) as ProductCardRendimentiType;

    return {
      informazioniPrincipali,
      anagrafica,
      rendimentiData: (rendimentiAndDrawDownData?.rendimenti ||
        []) as ProductCardArrayDateProductReturnCategoryReturnType[],
      drawdownData: (rendimentiAndDrawDownData?.drawdown ||
        []) as ProductCardArrayDateValueType[],
      rendimentiPerAnnoSolareData:
        (rendimentiAndDrawDownData?.rendimenti_per_anno_solare ||
          []) as ProductCardArrayDateProductReturnCategoryReturnType[],
      misureDiRischioData: (data?.misure_di_rischio ||
        []) as ProductCardLabelWeightType[],
      rendimentiCumulatiData: (data?.rendimenti_cumulati ||
        []) as ProductCardLabelWeightType[],
      distribuzioneAssetData: (data?.distribuzione_asset ||
        []) as ProductCardLabelWeightType[],
      esposizioneValutariaData: (data?.esposizione_valutaria ||
        []) as ProductCardLabelWeightType[],
      esposizioneGeograficaData: (data?.esposizione_geografica ||
        []) as ProductCardLabelWeightType[],
      esposizioneSettorialeParteAzionariaData:
        (data?.esposizione_settoriale_parte_azionaria ||
          []) as ProductCardLabelWeightType[],
      esposizioneSettorialeParteObbligazionariaData:
        (data?.esposizione_settoriale_parte_obbligazionaria ||
          []) as ProductCardLabelWeightType[],
      title:
        informazioniPrincipali.find((item) => item.label === "Nome")?.weight ||
        "",
      date:
        informazioniPrincipali.find((item) => item.label === "Date")?.weight ||
        "",
      isin:
        informazioniPrincipali.find((item) => item.label === "ISIN")?.weight || anagrafica.find((item) => item.label === "ISIN")?.weight ||
        "",
    };
  }, [data]);

  if (!data) {
    return null;
  }

  return (
    <div className={styles.cardContainer} ref={cardRef}>
      <div className={styles.cardHeader}>
        <h1 className={styles.cardTitle}>
          {title} - {t("productCard.product_analysis")}
        </h1>
        <div className={styles.cardDate}>
          {t("productCard.dataFrom")} {date}
        </div>
      </div>

      <div className="section-informazioni-generali">
        <InformazioniGenerali
          data={informazioniPrincipali}
          productType={data?.tipo_prodotto as string}
        />
      </div>
      {data?.tipo_prodotto === ProductTypeEnum.BOND && (
        <div className="section-anagrafica">
          <Anagrafica data={anagrafica} />
        </div>
      )}
      {data?.informazioni_gestore && (
        <div className="section-informazioni-gestore">
          <InformazioniGestore
            data={data.informazioni_gestore as InformazioniGestoreType}
          />
        </div>
      )}
      <div className="force-page-break"></div>
      <div className="section-rendimenti-chart">
        <RendimentiChart data={rendimentiData} isin={title} is_bond={data?.tipo_prodotto === ProductTypeEnum.BOND} />
      </div>
      <div className="section-drawdown-chart">
        <DrawdownChart data={drawdownData} />
      </div>

      <div className="page-break"></div>

      <div className={styles.cardPadding}>
        <RendimentiPerAnnoSolareChart
          data={rendimentiPerAnnoSolareData}
          isin={title}
          is_bond={data?.tipo_prodotto === ProductTypeEnum.BOND}
        />
      </div>

      <div className="section-risk-tables">
        <RiskTablesContainer
          misureDiRischio={misureDiRischioData}
          rendimentiCumulati={rendimentiCumulatiData}
          is_bond={data?.tipo_prodotto === ProductTypeEnum.BOND}
        />
      </div>

      <div className="page-break"></div>

      <div className={styles.cardPadding}>
        <GenericContainer
          data={distribuzioneAssetData}
          title={t("productCard.distribuzioneAsset")}
        />
      </div>

      <div className="section-currency-exposure">
        <CurrencyExposureContainer data={esposizioneValutariaData} />
      </div>

      <div className="page-break"></div>

      <div className={styles.cardPadding}>
        <GenericContainer
          data={esposizioneGeograficaData}
          title={t("productCard.esposizioneGeografica")}
        />
      </div>
      {data?.tipo_prodotto !== ProductTypeEnum.BOND && (
        <>
          <div className="section-esposizione-azionaria">
            <GenericContainer
              data={esposizioneSettorialeParteAzionariaData}
              title={t("productCard.esposizioneAzionaria")}
            />
          </div>

          <div className="page-break"></div>

          <div className={styles.cardPadding}>
            <GenericContainer
              data={esposizioneSettorialeParteObbligazionariaData}
              title={t("productCard.distribuzioneObbligazionaria")}
            />
          </div>
        </>
      )}

      <div className="page-break"></div>

      <div className={styles.cardPadding}>
        <FinecoProductCardButtons isin={isin} />
      </div>
    </div>
  );
});

FinecoProductCard.displayName = "FinecoProductCard";

export default FinecoProductCard;
